import { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  Wrench, Ticket, CheckCircle2, AlertTriangle,
  Clock, ChevronRight, ClipboardList, ArrowUpRight, Activity,
  Users, FileText, Zap, BarChart3, Star, Phone, MapPin,
  Loader2, RefreshCw, Inbox, XCircle, User
} from 'lucide-react'
import { ROUTES } from '@/constants'
import { useAuthStore } from '@/store/authStore'
import { taskService } from '@/services/firebase/tasks'
import { engineerService } from '@/services/firebase/engineers'
import type { Task, TaskStatus, TaskType } from '@/types'

// ── Config ────────────────────────────────────────────────────────────────────
const TYPE_MAP: Record<TaskType, { label: string; icon: string; grad: string; dot: string }> = {
  new_connection: { label: 'New Connection', icon: '🔌', grad: 'linear-gradient(135deg,#2563eb,#06b6d4)', dot: '#2563eb' },
  support:        { label: 'Support',        icon: '🛠️', grad: 'linear-gradient(135deg,#f97316,#ef4444)', dot: '#f97316' },
  enquiry:        { label: 'Enquiry',        icon: '💬', grad: 'linear-gradient(135deg,#8b5cf6,#6366f1)', dot: '#8b5cf6' },
  others:         { label: 'Others',         icon: '📋', grad: 'linear-gradient(135deg,#64748b,#475569)', dot: '#64748b' },
}

const STATUS_CFG: Record<TaskStatus, { label: string; bg: string; text: string; border: string; icon: React.ReactNode; dot: string }> = {
  open:             { label: 'Open',             bg: 'bg-sky-50',     text: 'text-sky-700',     border: 'border-sky-200',     icon: <Inbox size={10} />,        dot: '#0ea5e9' },
  assigned:         { label: 'Assigned',         bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    icon: <User size={10} />,         dot: '#3b82f6' },
  in_progress:      { label: 'In Progress',      bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200',  icon: <Zap size={10} />,          dot: '#8b5cf6' },
  site_visit:       { label: 'Site Visit',       bg: 'bg-indigo-50',  text: 'text-indigo-700',  border: 'border-indigo-200',  icon: <MapPin size={10} />,       dot: '#6366f1' },
  work_in_progress: { label: 'Work In Progress', bg: 'bg-orange-50',  text: 'text-orange-700',  border: 'border-orange-200',  icon: <Zap size={10} />,          dot: '#f97316' },
  pending_approval: { label: 'Pending Approval', bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   icon: <Clock size={10} />,        dot: '#f59e0b' },
  pending_feedback: { label: 'Pending Feedback', bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200',  icon: <Star size={10} />,         dot: '#a855f7' },
  closed:           { label: 'Closed',           bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: <CheckCircle2 size={10} />, dot: '#10b981' },
  cancelled:        { label: 'Cancelled',        bg: 'bg-gray-50',    text: 'text-gray-500',    border: 'border-gray-200',    icon: <XCircle size={10} />,      dot: '#9ca3af' },
}

const PRIORITY_BAR: Record<string, string> = {
  low: '#94a3b8', medium: '#3b82f6', high: '#f97316', urgent: '#ef4444',
}

const ACTIVE_STATUSES: TaskStatus[] = ['assigned','in_progress','site_visit','work_in_progress','pending_approval','pending_feedback']

// ── Hooks ─────────────────────────────────────────────────────────────────────
const useCountUp = (target: number, duration = 600) => {
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (target === 0) { setCount(0); return }
    let start: number | null = null
    const frame = requestAnimationFrame(function step(ts) {
      if (!start) start = ts
      const p = Math.min((ts - start) / duration, 1)
      setCount(Math.floor((1 - Math.pow(1 - p, 3)) * target))
      if (p < 1) requestAnimationFrame(step)
      else setCount(target)
    })
    return () => cancelAnimationFrame(frame)
  }, [target, duration])
  return count
}

const useClock = () => {
  const fmt = () => new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
  const [time, setTime] = useState(fmt)
  useEffect(() => { const id = setInterval(() => setTime(fmt()), 60000); return () => clearInterval(id) }, [])
  return time
}

// ── Stat pill (animated) ─────────────────────────────────────────────────────
const StatPill = ({ icon: Icon, label, value, color }: { icon: React.ElementType; label: string; value: string; color: string }) => (
  <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl"
    style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.09)' }}>
    <Icon size={14} className={color} />
    <div>
      <p className="text-[10px] text-blue-300/55 font-medium">{label}</p>
      <p className="text-sm font-black text-white leading-none mt-0.5">{value}</p>
    </div>
  </div>
)

// ── KPI card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, icon: Icon, grad, glow, path, sub, state }:
  { label: string; value: number; icon: React.ElementType; grad: string; glow: string; path: string; sub: string; state?: any }) => {
  const count = useCountUp(value)
  return (
    <Link to={path} state={state} className="group">
      <div className="relative bg-white rounded-2xl p-4 transition-all duration-200 hover:-translate-y-1 overflow-hidden"
        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)' }}>
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-2xl"
          style={{ background: `radial-gradient(circle at top right, ${glow} 0%, transparent 65%)` }} />
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center"
              style={{ background: grad, boxShadow: `0 4px 12px ${glow}` }}>
              <Icon size={18} className="text-white" />
            </div>
            <ArrowUpRight size={13} className="text-gray-300 group-hover:text-gray-500 transition-colors mt-0.5" />
          </div>
          <p className="text-3xl font-black text-gray-900 leading-none tabular-nums">{count}</p>
          <p className="text-xs font-semibold text-gray-500 mt-1.5 leading-tight">{label}</p>
          <p className="text-[10px] text-gray-400 mt-1">{sub}</p>
        </div>
      </div>
    </Link>
  )
}

// ── Assigned task row (engineer dashboard) ────────────────────────────────────
const AssignedTaskRow = ({ task, onClick }: { task: Task; onClick: () => void }) => {
  const t = TYPE_MAP[task.type]
  const s = STATUS_CFG[task.status]
  const p = PRIORITY_BAR[task.priority] ?? '#94a3b8'
  const initials = task.customerName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
  const displayType = task.type === 'others' && task.customType ? task.customType : t.label

  return (
    <div onClick={onClick}
      className="flex items-start gap-3 px-4 py-3.5 hover:bg-gray-50/70 transition-colors cursor-pointer group border-b border-gray-50 last:border-0">
      {/* Priority strip + avatar */}
      <div className="relative shrink-0">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-xs font-black"
          style={{ background: t.grad }}>
          {initials}
        </div>
        <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white"
          style={{ background: p }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-bold text-gray-900 truncate group-hover:text-blue-700 transition-colors">{task.customerName}</p>
          <span className={`shrink-0 flex items-center gap-1 text-[9px] font-black px-2 py-0.5 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
            {s.icon} {s.label}
          </span>
        </div>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-[10px] font-mono text-blue-500">{task.taskNumber}</span>
          <span className="text-[10px] text-gray-400">{t.icon} {displayType}</span>
        </div>
        <div className="flex items-center gap-3 mt-1">
          <span className="flex items-center gap-1 text-[10px] text-gray-400">
            <Phone size={9} /> +91 {task.customerPhone}
          </span>
          {task.city && (
            <span className="flex items-center gap-1 text-[10px] text-gray-400">
              <MapPin size={9} /> {task.city}
            </span>
          )}
        </div>
      </div>
      <ChevronRight size={14} className="text-gray-300 group-hover:text-blue-400 transition-colors shrink-0 self-center" />
    </div>
  )
}

// ── Quick action button ───────────────────────────────────────────────────────
const QuickAction = ({ label, desc, path, icon: Icon, grad }: {
  label: string; desc: string; path: string; icon: React.ElementType; grad: string
}) => (
  <Link to={path}
    className="flex items-center gap-3 px-4 py-3 rounded-2xl text-white transition-all hover:scale-[1.01] active:scale-[0.99] group"
    style={{ background: grad, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
    <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
      <Icon size={15} className="text-white" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-bold leading-tight">{label}</p>
      <p className="text-[11px] text-white/65 mt-0.5">{desc}</p>
    </div>
    <ChevronRight size={15} className="text-white/50 group-hover:translate-x-0.5 transition-transform shrink-0" />
  </Link>
)

// ── Main component ─────────────────────────────────────────────────────────────
const DashboardPage = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'admin'
  const engineerId = user?.uid ?? user?.id ?? ''
  const firstName = user?.name?.split(' ')[0] || 'there'
  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })
  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'
  const time = useClock()

  // ── Data state ──────────────────────────────────────────────────────────────
  const [allTasks, setAllTasks] = useState<Task[]>([])
  const [myTasks, setMyTasks] = useState<Task[]>([])
  const [activeEngCount, setActiveEngCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    if (isAdmin) {
      const [taskRes, engRes] = await Promise.all([
        taskService.list(),
        engineerService.list(),
      ])
      if (taskRes.success && taskRes.data) setAllTasks(taskRes.data)
      if (engRes.success && engRes.data) setActiveEngCount(engRes.data.filter(e => e.isActive && e.role === 'engineer').length)
    } else {
      const myIds = [engineerId, user?.engineerDocId].filter(Boolean) as string[]
      const res = await taskService.listByAssignee(engineerId, user?.engineerDocId)
      if (res.success && res.data) {
        const filtered = res.data.filter(t => t.assignedTo && myIds.includes(t.assignedTo))
        filtered.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        setMyTasks(filtered)
      }
    }
    setLoading(false)
  }, [isAdmin, engineerId, user?.engineerDocId])

  useEffect(() => { load() }, [load])

  // ── Derived admin KPIs ───────────────────────────────────────────────────────
  const openTasks   = allTasks.filter(t => t.status === 'open').length
  const activeTasks = allTasks.filter(t => ACTIVE_STATUSES.includes(t.status)).length
  const closedToday = allTasks.filter(t => {
    if (t.status !== 'closed' || !t.closedAt) return false
    const d = new Date(); return t.closedAt.toDateString() === d.toDateString()
  }).length
  const closedAll   = allTasks.filter(t => t.status === 'closed').length
  const tickets     = allTasks.filter(t => t.type === 'support')
  const openTickets = tickets.filter(t => !['closed','cancelled'].includes(t.status)).length
  const installs    = allTasks.filter(t => t.type === 'new_connection')
  const pendingInstalls = installs.filter(t => !['closed','cancelled'].includes(t.status)).length
  const enquiries   = allTasks.filter(t => t.type === 'enquiry')
  const openEnquiries = enquiries.filter(t => !['closed','cancelled'].includes(t.status)).length
  const urgentCount = allTasks.filter(t => t.priority === 'urgent' && !['closed','cancelled'].includes(t.status)).length
  const needsApproval = allTasks.filter(t => t.status === 'pending_approval').length

  // Avg CSAT from closed tasks with ratings
  const ratedTasks = allTasks.filter(t => t.customerRating != null)
  const avgCsat = ratedTasks.length > 0
    ? (ratedTasks.reduce((s, t) => s + (t.customerRating ?? 0), 0) / ratedTasks.length).toFixed(1)
    : '—'

  // Breakdown by type for admin
  const typeBreakdown = [
    { label: 'Installations', count: installs.length, active: pendingInstalls, grad: 'linear-gradient(135deg,#2563eb,#06b6d4)', icon: '🔌' },
    { label: 'Support',       count: tickets.length,  active: openTickets,    grad: 'linear-gradient(135deg,#f97316,#ef4444)', icon: '🛠️' },
    { label: 'Enquiries',     count: enquiries.length,active: openEnquiries,  grad: 'linear-gradient(135deg,#8b5cf6,#6366f1)', icon: '💬' },
  ]

  // ── Engineer stats ───────────────────────────────────────────────────────────
  const myActive = myTasks.filter(t => ACTIVE_STATUSES.includes(t.status))
  const myDone   = myTasks.filter(t => t.status === 'closed')
  const myPendingApproval = myTasks.filter(t => t.status === 'pending_approval')

  // Recent closed tasks (last 7 days) for engineer
  const recentClosed = myTasks.filter(t => {
    if (t.status !== 'closed' || !t.closedAt) return false
    return Date.now() - t.closedAt.getTime() < 7 * 86400000
  })

  return (
    <div className="space-y-5 pb-8">

      {/* ── Hero banner ──────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl text-white"
        style={{ background: 'linear-gradient(135deg,#050d1f 0%,#0c1e3d 45%,#0d2a50 100%)', minHeight: 160 }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(59,130,246,0.35) 0%,transparent 70%)', filter: 'blur(48px)' }} />
          <div className="absolute -bottom-8 left-1/4 w-64 h-64 rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(6,182,212,0.2) 0%,transparent 70%)', filter: 'blur(40px)' }} />
          <div className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>

        <div className="relative z-10 p-5 lg:p-7 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-3 py-1">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                <span className="text-[11px] text-blue-200 font-semibold uppercase tracking-wider">{today}</span>
              </div>
              <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/10 rounded-full px-3 py-1">
                <Clock size={11} className="text-blue-300" />
                <span className="text-[11px] text-blue-200 font-semibold">{time}</span>
              </div>
              {loading && (
                <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/10 rounded-full px-3 py-1">
                  <Loader2 size={10} className="text-blue-300 animate-spin" />
                  <span className="text-[11px] text-blue-200 font-semibold">Loading…</span>
                </div>
              )}
            </div>
            <h1 className="text-2xl lg:text-3xl font-black leading-tight tracking-tight">
              {greeting},{' '}
              <span style={{ background: 'linear-gradient(135deg,#93c5fd,#67e8f9)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                {firstName}
              </span> {isAdmin ? '👋' : '👷'}
            </h1>
            <p className="text-blue-300/70 text-sm mt-1.5 max-w-sm">
              {isAdmin
                ? `${activeTasks} active tasks across the team · ${openTasks} open & unassigned`
                : `${myActive.length} active assignment${myActive.length !== 1 ? 's' : ''} · ${myDone.length} completed`}
            </p>
            {isAdmin ? (
              <div className="flex items-center gap-2 mt-4">
                <Link to={ROUTES.NEW_TASK}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', boxShadow: '0 4px 14px rgba(59,130,246,0.4)' }}>
                  <Zap size={14} /> New Task
                </Link>
                <Link to={ROUTES.REPORTS}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white/80 hover:text-white bg-white/10 hover:bg-white/15 border border-white/10 transition-all">
                  <BarChart3 size={14} /> Reports
                </Link>
                <button onClick={load}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm text-white/60 hover:text-white bg-white/10 hover:bg-white/15 border border-white/10 transition-all">
                  <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-4">
                <Link to={ROUTES.TASKS}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)', boxShadow: '0 4px 14px rgba(139,92,246,0.4)' }}>
                  <ClipboardList size={14} /> My Tasks
                </Link>
                <button onClick={load}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm text-white/60 hover:text-white bg-white/10 hover:bg-white/15 border border-white/10 transition-all">
                  <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                </button>
              </div>
            )}
          </div>

          {/* Right: real metric pills */}
          <div className="flex lg:flex-col gap-2 flex-wrap lg:flex-nowrap">
            {isAdmin ? (
              <>
                <StatPill icon={Users}      label="Active Engineers"  value={String(activeEngCount)}       color="text-cyan-300" />
                <StatPill icon={CheckCircle2} label="Closed Today"    value={String(closedToday)}          color="text-emerald-300" />
                <StatPill icon={AlertTriangle} label="Urgent Open"    value={String(urgentCount)}          color="text-red-300" />
                <StatPill icon={Star}        label="Avg CSAT"         value={avgCsat === '—' ? '—' : `${avgCsat}★`} color="text-yellow-300" />
              </>
            ) : (
              <>
                <StatPill icon={ClipboardList} label="Active Tasks"   value={String(myActive.length)}      color="text-violet-300" />
                <StatPill icon={CheckCircle2}  label="Closed (7 days)" value={String(recentClosed.length)} color="text-emerald-300" />
                <StatPill icon={Clock}         label="Awaiting Approval" value={String(myPendingApproval.length)} color="text-amber-300" />
              </>
            )}
          </div>
        </div>
      </div>

      {/* ══════════════ ADMIN VIEW ══════════════ */}
      {isAdmin && (
        <>
          {/* KPI grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
            <KpiCard label="Open Tasks"       value={openTasks}        icon={ClipboardList}  path={ROUTES.TASKS}         state={{ status: 'open' }} grad="linear-gradient(135deg,#2563eb,#1d4ed8)"  glow="rgba(37,99,235,0.22)"   sub="Unassigned" />
            <KpiCard label="Active Tasks"     value={activeTasks}      icon={Zap}            path={ROUTES.TASKS}         state={{ status: 'in_progress' }} grad="linear-gradient(135deg,#8b5cf6,#6366f1)"  glow="rgba(139,92,246,0.22)"  sub="In progress" />
            <KpiCard label="Open Tickets"     value={openTickets}      icon={Ticket}         path={ROUTES.TICKETS}       state={{ status: 'open' }} grad="linear-gradient(135deg,#ef4444,#dc2626)"  glow="rgba(239,68,68,0.22)"   sub="Support tasks" />
            <KpiCard label="Pending Installs" value={pendingInstalls}  icon={Wrench}         path={ROUTES.INSTALLATIONS} state={{ status: 'pending_approval' }} grad="linear-gradient(135deg,#f97316,#ea580c)"  glow="rgba(249,115,22,0.22)"  sub="New connections" />
            <KpiCard label="Open Enquiries"   value={openEnquiries}    icon={FileText}       path={ROUTES.INQUIRIES}     state={{ status: 'open' }} grad="linear-gradient(135deg,#0ea5e9,#06b6d4)"  glow="rgba(14,165,233,0.22)"  sub="Feasibility pending" />
            <KpiCard label="Need Approval"    value={needsApproval}    icon={AlertTriangle}  path={ROUTES.TASKS}         state={{ status: 'pending_approval' }} grad="linear-gradient(135deg,#f59e0b,#d97706)"  glow="rgba(245,158,11,0.22)"  sub="Awaiting review" />
          </div>

          {/* Second row: Type breakdown + recent closed + quick actions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* Task type breakdown */}
            <div className="lg:col-span-2 space-y-4">

              {/* Breakdown cards */}
              <div className="grid grid-cols-3 gap-3">
                {typeBreakdown.map(tb => (
                  <div key={tb.label} className="bg-white rounded-2xl p-4 overflow-hidden relative"
                    style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)' }}>
                    <div className="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl" style={{ background: tb.grad }} />
                    <p className="text-2xl mt-2">{tb.icon}</p>
                    <p className="text-2xl font-black text-gray-900 mt-2 tabular-nums">{tb.count}</p>
                    <p className="text-xs font-semibold text-gray-500 mt-0.5">{tb.label}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{tb.active} active</p>
                  </div>
                ))}
              </div>

              {/* Recent tasks requiring attention */}
              <div className="bg-white rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)' }}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg,rgba(245,158,11,0.12),rgba(249,115,22,0.08))', border: '1px solid rgba(245,158,11,0.15)' }}>
                      <AlertTriangle size={14} className="text-amber-600" />
                    </div>
                    <div>
                      <h2 className="font-black text-gray-900 text-sm">Needs Attention</h2>
                      <p className="text-[10px] text-gray-400">Urgent + pending approval</p>
                    </div>
                  </div>
                  <Link to={ROUTES.TASKS} className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1">
                    All tasks <ChevronRight size={12} />
                  </Link>
                </div>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 size={20} className="text-gray-300 animate-spin" />
                  </div>
                ) : (() => {
                  const attention = allTasks
                    .filter(t => (t.status === 'pending_approval' || t.priority === 'urgent') && !['closed','cancelled'].includes(t.status))
                    .sort((a,b) => {
                      if (a.status === 'pending_approval' && b.status !== 'pending_approval') return -1
                      if (b.status === 'pending_approval' && a.status !== 'pending_approval') return 1
                      return b.createdAt.getTime() - a.createdAt.getTime()
                    })
                    .slice(0, 5)
                  return attention.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 gap-2">
                      <CheckCircle2 size={24} className="text-emerald-400" />
                      <p className="text-sm text-gray-400">All clear — no urgent items</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-50">
                      {attention.map(task => {
                        const t = TYPE_MAP[task.type]
                        const s = STATUS_CFG[task.status]
                        const initials = task.customerName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
                        return (
                          <div key={task.id}
                            onClick={() => navigate(`/tasks/${task.id}`)}
                            className="flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50/60 transition-colors cursor-pointer group">
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-black shrink-0"
                              style={{ background: t.grad }}>
                              {initials}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-bold text-gray-900 truncate group-hover:text-blue-700 transition-colors">{task.customerName}</p>
                                {task.priority === 'urgent' && (
                                  <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 shrink-0">URGENT</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] font-mono text-blue-500">{task.taskNumber}</span>
                                <span className="text-[10px] text-gray-400">{t.icon} {t.label}</span>
                              </div>
                            </div>
                            <span className={`shrink-0 flex items-center gap-1 text-[9px] font-black px-2 py-1 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
                              {s.icon} {s.label}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  )
                })()}
              </div>

              {/* Recently closed */}
              <div className="bg-white rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)' }}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg,rgba(16,185,129,0.12),rgba(5,150,105,0.08))', border: '1px solid rgba(16,185,129,0.15)' }}>
                      <CheckCircle2 size={14} className="text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="font-black text-gray-900 text-sm">Recently Closed</h2>
                      <p className="text-[10px] text-gray-400">{closedAll} total completed</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">
                    {closedToday} today
                  </span>
                </div>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 size={20} className="text-gray-300 animate-spin" />
                  </div>
                ) : (() => {
                  const recent = allTasks.filter(t => t.status === 'closed')
                    .sort((a,b) => (b.closedAt ?? b.updatedAt).getTime() - (a.closedAt ?? a.updatedAt).getTime())
                    .slice(0, 5)
                  return recent.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 gap-2">
                      <Activity size={24} className="text-gray-200" />
                      <p className="text-sm text-gray-400">No closed tasks yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-50">
                      {recent.map(task => {
                        const t = TYPE_MAP[task.type]
                        const initials = task.customerName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
                        const closedDate = task.closedAt ?? task.updatedAt
                        const hoursAgo = Math.floor((Date.now() - closedDate.getTime()) / 3600000)
                        const timeStr = hoursAgo < 1 ? 'Just now' : hoursAgo < 24 ? `${hoursAgo}h ago` : closedDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
                        return (
                          <div key={task.id}
                            onClick={() => navigate(`/tasks/${task.id}`)}
                            className="flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50/60 transition-colors cursor-pointer group">
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-black shrink-0"
                              style={{ background: t.grad }}>
                              {initials}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-gray-900 truncate group-hover:text-blue-700 transition-colors">{task.customerName}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] font-mono text-blue-500">{task.taskNumber}</span>
                                <span className="text-[10px] text-gray-400">{t.icon} {t.label}</span>
                                {task.assignedEngineerName && <span className="text-[10px] text-gray-400">· {task.assignedEngineerName}</span>}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-[10px] text-gray-400">{timeStr}</p>
                              {task.customerRating && (
                                <p className="text-[10px] text-amber-500 font-black mt-0.5">{'★'.repeat(task.customerRating)}</p>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })()}
              </div>
            </div>

            {/* Right sidebar: Quick actions + status breakdown */}
            <div className="space-y-4">
              <div className="bg-white rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)' }}>
                <div className="flex items-center gap-2.5 px-5 py-4 border-b border-gray-50">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg,rgba(37,99,235,0.1),rgba(99,102,241,0.08))', border: '1px solid rgba(37,99,235,0.12)' }}>
                    <Zap size={14} className="text-blue-600" />
                  </div>
                  <div>
                    <h2 className="font-black text-gray-900 text-sm">Quick Actions</h2>
                    <p className="text-[10px] text-gray-400">Jump to key workflows</p>
                  </div>
                </div>
                <div className="p-3 space-y-2">
                  <QuickAction label="New Task"     desc="Create & assign a field task"  path={ROUTES.NEW_TASK}    icon={ClipboardList} grad="linear-gradient(135deg,#2563eb,#1d4ed8)" />
                  <QuickAction label="New Enquiry"  desc="Register customer interest"    path={ROUTES.INQUIRIES}   icon={FileText}      grad="linear-gradient(135deg,#6366f1,#4f46e5)" />
                  <QuickAction label="View Reports" desc="Analytics & performance"       path={ROUTES.REPORTS}     icon={BarChart3}     grad="linear-gradient(135deg,#8b5cf6,#7c3aed)" />
                  <QuickAction label="Engineers"    desc="Manage field team"             path={ROUTES.ENGINEERS}   icon={Users}         grad="linear-gradient(135deg,#0ea5e9,#06b6d4)" />
                </div>
              </div>

              {/* Status distribution */}
              <div className="bg-white rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)' }}>
                <div className="px-5 py-4 border-b border-gray-50">
                  <h2 className="font-black text-gray-900 text-sm">Status Distribution</h2>
                  <p className="text-[10px] text-gray-400 mt-0.5">{allTasks.length} total tasks</p>
                </div>
                <div className="p-4 space-y-2.5">
                  {([
                    ['open','Open'],['assigned','Assigned'],['in_progress','In Progress'],
                    ['pending_approval','Pending Approval'],['closed','Closed'],['cancelled','Cancelled'],
                  ] as [TaskStatus, string][]).map(([st, label]) => {
                    const cnt = allTasks.filter(t => t.status === st).length
                    const pct = allTasks.length > 0 ? Math.round((cnt / allTasks.length) * 100) : 0
                    const s = STATUS_CFG[st]
                    return (
                      <div key={st}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] font-semibold text-gray-600">{label}</span>
                          <span className="text-[11px] font-black text-gray-800 tabular-nums">{cnt}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${pct}%`, background: s.dot }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ══════════════ ENGINEER VIEW ══════════════ */}
      {!isAdmin && (
        <>
          {/* Personal stats row */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Active',    value: myActive.length,          grad: 'linear-gradient(135deg,#8b5cf6,#6366f1)', icon: Zap },
              { label: 'Completed', value: myDone.length,            grad: 'linear-gradient(135deg,#10b981,#059669)', icon: CheckCircle2 },
              { label: 'Total',     value: myTasks.length,           grad: 'linear-gradient(135deg,#2563eb,#06b6d4)', icon: ClipboardList },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-2xl p-4 relative overflow-hidden"
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)' }}>
                <div className="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl" style={{ background: s.grad }} />
                <div className="w-8 h-8 rounded-xl flex items-center justify-center mt-2 mb-2"
                  style={{ background: s.grad }}>
                  <s.icon size={15} className="text-white" />
                </div>
                <p className="text-2xl font-black text-gray-900 tabular-nums">{s.value}</p>
                <p className="text-xs font-semibold text-gray-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* Active assignments — main panel */}
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)' }}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg,rgba(139,92,246,0.12),rgba(99,102,241,0.08))', border: '1px solid rgba(139,92,246,0.15)' }}>
                      <ClipboardList size={14} className="text-violet-600" />
                    </div>
                    <div>
                      <h2 className="font-black text-gray-900 text-sm">Active Assignments</h2>
                      <p className="text-[10px] text-gray-400">{myActive.length} task{myActive.length !== 1 ? 's' : ''} in progress</p>
                    </div>
                  </div>
                  <Link to={ROUTES.TASKS}
                    className="text-xs font-bold text-violet-600 hover:text-violet-800 flex items-center gap-1">
                    All tasks <ChevronRight size={12} />
                  </Link>
                </div>

                {loading ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <Loader2 size={22} className="text-violet-400 animate-spin" />
                    <p className="text-sm text-gray-400">Loading your tasks…</p>
                  </div>
                ) : myActive.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-14 gap-3 text-center px-5">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg,rgba(139,92,246,0.08),rgba(99,102,241,0.05))', border: '1px solid rgba(139,92,246,0.1)' }}>
                      <ClipboardList size={22} className="text-gray-300" />
                    </div>
                    <div>
                      <p className="font-black text-gray-700">No active tasks</p>
                      <p className="text-sm text-gray-400 mt-1">Check the Tasks tab to claim open work</p>
                    </div>
                    <Link to={ROUTES.TASKS}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white rounded-xl"
                      style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)' }}>
                      <Inbox size={14} /> View Open Tasks
                    </Link>
                  </div>
                ) : (
                  myActive.map(task => (
                    <AssignedTaskRow key={task.id} task={task} onClick={() => navigate(`/tasks/${task.id}`)} />
                  ))
                )}
              </div>

              {/* Pending approval banner */}
              {myPendingApproval.length > 0 && (
                <div className="rounded-2xl overflow-hidden border border-amber-200 bg-amber-50">
                  <div className="flex items-center gap-3 px-4 py-3 border-b border-amber-100">
                    <Clock size={14} className="text-amber-600 shrink-0" />
                    <p className="text-sm font-black text-amber-800">
                      {myPendingApproval.length} task{myPendingApproval.length !== 1 ? 's' : ''} awaiting admin approval
                    </p>
                  </div>
                  {myPendingApproval.map(task => (
                    <AssignedTaskRow key={task.id} task={task} onClick={() => navigate(`/tasks/${task.id}`)} />
                  ))}
                </div>
              )}

              {/* Recently closed */}
              {recentClosed.length > 0 && (
                <div className="bg-white rounded-2xl overflow-hidden"
                  style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)' }}>
                  <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg,rgba(16,185,129,0.12),rgba(5,150,105,0.08))', border: '1px solid rgba(16,185,129,0.15)' }}>
                      <CheckCircle2 size={14} className="text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="font-black text-gray-900 text-sm">Completed This Week</h2>
                      <p className="text-[10px] text-gray-400">{recentClosed.length} task{recentClosed.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  {recentClosed.map(task => (
                    <AssignedTaskRow key={task.id} task={task} onClick={() => navigate(`/tasks/${task.id}`)} />
                  ))}
                </div>
              )}
            </div>

            {/* Engineer right sidebar */}
            <div className="space-y-4">
              {/* Quick actions */}
              <div className="bg-white rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)' }}>
                <div className="flex items-center gap-2.5 px-5 py-4 border-b border-gray-50">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg,rgba(139,92,246,0.1),rgba(99,102,241,0.08))', border: '1px solid rgba(139,92,246,0.12)' }}>
                    <Zap size={14} className="text-violet-600" />
                  </div>
                  <div>
                    <h2 className="font-black text-gray-900 text-sm">Quick Actions</h2>
                    <p className="text-[10px] text-gray-400">Field workflows</p>
                  </div>
                </div>
                <div className="p-3 space-y-2">
                  <QuickAction label="My Tasks"    desc="View all assigned work"      path={ROUTES.TASKS}       icon={ClipboardList} grad="linear-gradient(135deg,#8b5cf6,#6366f1)" />
                  <QuickAction label="New Enquiry" desc="Register customer interest"  path={ROUTES.INQUIRIES}   icon={FileText}      grad="linear-gradient(135deg,#6366f1,#4f46e5)" />
                  <QuickAction label="Raise Ticket" desc="Report a service issue"     path={ROUTES.TICKETS}     icon={Ticket}        grad="linear-gradient(135deg,#ef4444,#dc2626)" />
                </div>
              </div>

              {/* Task priority breakdown */}
              <div className="bg-white rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)' }}>
                <div className="px-5 py-4 border-b border-gray-50">
                  <h2 className="font-black text-gray-900 text-sm">My Task Priorities</h2>
                  <p className="text-[10px] text-gray-400 mt-0.5">Active only</p>
                </div>
                <div className="p-4 space-y-2.5">
                  {(['urgent','high','medium','low'] as const).map(pr => {
                    const cnt = myActive.filter(t => t.priority === pr).length
                    const pct = myActive.length > 0 ? Math.round((cnt / myActive.length) * 100) : 0
                    const bar = PRIORITY_BAR[pr]
                    return (
                      <div key={pr}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] font-semibold text-gray-600 capitalize">{pr}</span>
                          <span className="text-[11px] font-black text-gray-800 tabular-nums">{cnt}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${pct}%`, background: bar }} />
                        </div>
                      </div>
                    )
                  })}
                  {myActive.length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-2">No active tasks</p>
                  )}
                </div>
              </div>

              {/* Personal rating */}
              {(() => {
                const myRated = myTasks.filter(t => t.customerRating != null)
                if (myRated.length === 0) return null
                const avg = (myRated.reduce((s,t) => s + (t.customerRating ?? 0), 0) / myRated.length).toFixed(1)
                return (
                  <div className="rounded-2xl p-5 relative overflow-hidden"
                    style={{ background: 'linear-gradient(135deg,#0c1e3d 0%,#0d2a50 100%)' }}>
                    <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full pointer-events-none"
                      style={{ background: 'radial-gradient(circle,rgba(245,158,11,0.3) 0%,transparent 70%)', filter: 'blur(16px)' }} />
                    <div className="relative z-10">
                      <p className="text-[10px] font-black text-blue-300/60 uppercase tracking-widest mb-3">My CSAT Rating</p>
                      <div className="flex items-end gap-2">
                        <p className="text-4xl font-black text-white">{avg}</p>
                        <p className="text-amber-400 text-xl mb-1">★</p>
                      </div>
                      <p className="text-xs text-blue-300/50 mt-1">from {myRated.length} rated job{myRated.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                )
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default DashboardPage
